import React from "react";
import { ChevronDown, ChevronUp, ExternalLink, Mail, User } from "lucide-react";
import {
  extractGuestFromPayload,
  extractOccupancyFromPayload,
  extractRoomLinesFromPayload,
  hasChargeBreakdown,
} from "../../../utils/warehouseReservationDetails";
import { extractCurrencyFromPayload } from "../../../utils/warehouseReservationCharges";
import { WarehouseReservationDailyCharges } from "./WarehouseReservationDailyCharges";

type DetailsLocale = "en" | "fr";

const LABELS: Record<
  DetailsLocale,
  {
    client: string;
    viewCrmProfile: string;
    noEmailForCrm: string;
    roomCategories: string;
    occupants: string;
    adults: string;
    children: string;
    infants: string;
    nights: string;
    roomType: string;
    rate: string;
    room: string;
    rooms: string;
    amount: string;
    normalizedPayload: string;
    loading: string;
    noClient: string;
    noRooms: string;
  }
> = {
  en: {
    client: "Client",
    viewCrmProfile: "View CRM profile",
    noEmailForCrm: "No email available for CRM profile",
    roomCategories: "Room categories",
    occupants: "Occupants",
    adults: "Adults",
    children: "Children",
    infants: "Infants",
    nights: "Nights",
    roomType: "Category",
    rate: "Rate",
    room: "Room",
    rooms: "Rooms",
    amount: "Amount",
    normalizedPayload: "Normalized payload",
    loading: "Loading...",
    noClient: "No client details in payload",
    noRooms: "No room category details in payload",
  },
  fr: {
    client: "Client",
    viewCrmProfile: "Voir le profil CRM",
    noEmailForCrm: "Aucun e-mail disponible pour le profil CRM",
    roomCategories: "Catégories de chambre",
    occupants: "Occupants",
    adults: "Adultes",
    children: "Enfants",
    infants: "Bébés",
    nights: "Nuitées",
    roomType: "Catégorie",
    rate: "Tarif",
    room: "Chambre",
    rooms: "Chambres",
    amount: "Montant",
    normalizedPayload: "Payload normalisé",
    loading: "Chargement...",
    noClient: "Aucune information client dans le payload",
    noRooms: "Aucune catégorie de chambre dans le payload",
  },
};

function formatMoney(value: number, currency: string): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
      maximumFractionDigits: 2,
    }).format(value);
  } catch {
    return `${value.toFixed(2)} ${currency}`;
  }
}

interface WarehouseReservationDetailsContentProps {
  payload: unknown;
  loading?: boolean;
  locale?: DetailsLocale;
  payloadSectionOpen: boolean;
  onTogglePayloadSection: () => void;
  onOpenCrmProfile?: (email: string) => void;
  compact?: boolean;
}

export function WarehouseReservationDetailsContent({
  payload,
  loading = false,
  locale = "en",
  payloadSectionOpen,
  onTogglePayloadSection,
  onOpenCrmProfile,
  compact = false,
}: WarehouseReservationDetailsContentProps) {
  const labels = LABELS[locale];
  const guest = extractGuestFromPayload(payload);
  const roomLines = extractRoomLinesFromPayload(payload);
  const occupancy = extractOccupancyFromPayload(payload);
  const currency = extractCurrencyFromPayload(payload);
  const showCharges = hasChargeBreakdown(payload);

  return (
    <div className={compact ? "space-y-3" : ""}>
      <div className={`${compact ? "" : "px-4 py-3 border-b border-gray-200"}`}>
        <div className="text-sm font-semibold text-gray-900 mb-2">{labels.client}</div>
        {loading && !guest ? (
          <div className="text-xs text-gray-500">{labels.loading}</div>
        ) : guest ? (
          <div className="rounded-md border border-gray-200 bg-white p-3 space-y-2">
            <div className="flex items-start gap-2">
              <User className="h-4 w-4 text-gray-400 mt-0.5 shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-gray-900">
                  {guest.displayName || guest.companyName || "—"}
                </div>
                {guest.companyName && guest.displayName && (
                  <div className="text-xs text-gray-500">{guest.companyName}</div>
                )}
                {guest.email && (
                  <div className="flex items-center gap-1.5 text-xs text-gray-600 mt-1">
                    <Mail className="h-3.5 w-3.5" />
                    <span className="truncate">{guest.email}</span>
                  </div>
                )}
                {(guest.phone || guest.country) && (
                  <div className="text-xs text-gray-500 mt-1">
                    {[guest.phone, guest.country].filter(Boolean).join(" · ")}
                  </div>
                )}
              </div>
            </div>
            {guest.email && onOpenCrmProfile ? (
              <button
                type="button"
                onClick={() => onOpenCrmProfile(guest.email!)}
                className="inline-flex items-center gap-1.5 text-xs font-medium text-indigo-600 hover:text-indigo-800"
              >
                <ExternalLink className="h-3.5 w-3.5" />
                {labels.viewCrmProfile}
              </button>
            ) : (
              !guest.email &&
              onOpenCrmProfile && (
                <div className="text-xs text-gray-400">{labels.noEmailForCrm}</div>
              )
            )}
          </div>
        ) : (
          <div className="text-xs text-gray-500">{labels.noClient}</div>
        )}
      </div>

      <div className={`${compact ? "" : "px-4 py-3 border-b border-gray-200"}`}>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
          <div>
            <div className="text-[11px] uppercase tracking-wide text-gray-400">
              {labels.adults}
            </div>
            <div className="text-sm font-semibold text-gray-900">{occupancy.adults ?? "—"}</div>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-wide text-gray-400">
              {labels.children}
            </div>
            <div className="text-sm font-semibold text-gray-900">
              {occupancy.children ?? "—"}
            </div>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-wide text-gray-400">
              {labels.infants}
            </div>
            <div className="text-sm font-semibold text-gray-900">{occupancy.infants ?? "—"}</div>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-wide text-gray-400">
              {labels.nights}
            </div>
            <div className="text-sm font-semibold text-gray-900">{occupancy.nights ?? "—"}</div>
          </div>
        </div>

        <div className="text-sm font-semibold text-gray-900 mb-2">{labels.roomCategories}</div>
        {loading && !roomLines.length ? (
          <div className="text-xs text-gray-500">{labels.loading}</div>
        ) : roomLines.length ? (
          <div className="space-y-2">
            {roomLines.map((line, index) => (
              <div
                key={`${line.roomTypeLabel}-${line.rateLabel}-${index}`}
                className="rounded-md border border-gray-200 bg-white p-3 grid grid-cols-2 gap-2 text-xs"
              >
                <div>
                  <div className="text-gray-400">{labels.roomType}</div>
                  <div className="font-medium text-gray-800">{line.roomTypeLabel || "—"}</div>
                </div>
                <div>
                  <div className="text-gray-400">{labels.rate}</div>
                  <div className="font-medium text-gray-800">{line.rateLabel || "—"}</div>
                </div>
                {line.roomName && (
                  <div>
                    <div className="text-gray-400">{labels.room}</div>
                    <div className="font-medium text-gray-800">{line.roomName}</div>
                  </div>
                )}
                {line.numberOfRooms != null && (
                  <div>
                    <div className="text-gray-400">{labels.rooms}</div>
                    <div className="font-medium text-gray-800">{line.numberOfRooms}</div>
                  </div>
                )}
                {(line.adults != null || line.children != null || line.infants != null) && (
                  <div className="col-span-2 text-gray-600">
                    {labels.occupants}:{" "}
                    {[line.adults, line.children, line.infants]
                      .map((value, idx) =>
                        value != null
                          ? `${value} ${idx === 0 ? labels.adults.toLowerCase() : idx === 1 ? labels.children.toLowerCase() : labels.infants.toLowerCase()}`
                          : null,
                      )
                      .filter(Boolean)
                      .join(" · ") || "—"}
                  </div>
                )}
                {line.amountAfterTax != null && (
                  <div>
                    <div className="text-gray-400">{labels.amount}</div>
                    <div className="font-medium text-gray-800">
                      {formatMoney(line.amountAfterTax, currency)}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-xs text-gray-500">{labels.noRooms}</div>
        )}
      </div>

      {showCharges && (
        <WarehouseReservationDailyCharges payload={payload} loading={loading} locale={locale} />
      )}

      <div className={`${compact ? "pt-1" : "px-4 py-3"}`}>
        <button
          type="button"
          onClick={onTogglePayloadSection}
          className="flex items-center gap-2 text-xs font-medium text-gray-500 hover:text-gray-700"
        >
          {payloadSectionOpen ? (
            <ChevronUp className="h-3.5 w-3.5" />
          ) : (
            <ChevronDown className="h-3.5 w-3.5" />
          )}
          {labels.normalizedPayload}
        </button>
        {payloadSectionOpen && (
          <div className="mt-2">
            {loading ? (
              <div className="text-xs text-gray-500 py-2">{labels.loading}</div>
            ) : (
              <pre className="bg-gray-50 p-3 rounded border border-gray-200 text-xs overflow-x-auto max-h-64">
                {JSON.stringify(payload, null, 2)}
              </pre>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
